import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Award, ShieldCheck, ShieldAlert, Loader2, ArrowLeft, Linkedin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatIssued(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return iso;
  }
}

function buildLinkedInAddUrl(cert) {
  const name = cert.kind === "course"
    ? "Certificate of Completion — Full Toolbox Curriculum (Social Impact Assessment)"
    : `Certificate of Completion — Module ${cert.module_number}: ${cert.module_title}`;
  const issued = cert.issued_at ? new Date(cert.issued_at) : new Date();
  const params = new URLSearchParams({
    startTask: "CERTIFICATION_NAME",
    name,
    organizationName: cert.issuer || "All Things Social Impact",
    issueYear: String(issued.getUTCFullYear()),
    issueMonth: String(issued.getUTCMonth() + 1),
    certUrl: `${window.location.origin}/verify/${cert.id}`,
    certId: cert.id,
  });
  return `https://www.linkedin.com/profile/add?${params.toString()}`;
}

export default function Verify() {
  const { certId } = useParams();
  const [state, setState] = useState({ loading: true, error: null, cert: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: null, cert: null });
    axios.get(`${API}/certificates/${encodeURIComponent(certId)}`)
      .then(({ data }) => { if (alive) setState({ loading: false, error: null, cert: data }); })
      .catch((e) => {
        if (!alive) return;
        const msg = e?.response?.status === 404
          ? "This certificate ID could not be found. Please double-check the code."
          : e?.response?.data?.detail || "Could not verify this certificate.";
        setState({ loading: false, error: msg, cert: null });
      });
    return () => { alive = false; };
  }, [certId]);

  return (
    <div data-testid="verify-page" className="min-h-[70vh] bg-slate-50 py-16 px-6">
      <div className="max-w-2xl mx-auto">
        <Link to="/" data-testid="verify-back-home"
              className="text-xs uppercase tracking-widest font-semibold text-slate-500 hover:text-brand-orange inline-flex items-center gap-1.5">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>

        <div className="mt-6 bg-white border border-slate-200 shadow-sm p-8 md:p-12">
          <div className="eyebrow mb-3">Certificate verification</div>

          {state.loading && (
            <div data-testid="verify-loading" className="flex items-center gap-3 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" /> Checking certificate…
            </div>
          )}

          {!state.loading && state.error && (
            <div data-testid="verify-invalid" className="border-l-4 border-red-500 bg-red-50 p-5 flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div>
                <div className="font-semibold text-red-900 mb-1">Not a valid certificate</div>
                <div className="text-sm text-red-800">{state.error}</div>
                <div className="text-xs text-red-700 mt-2 font-mono">ID: {certId}</div>
              </div>
            </div>
          )}

          {!state.loading && state.cert && (
            <div data-testid="verify-valid">
              <div className="border-l-4 border-emerald-500 bg-emerald-50 p-5 flex items-start gap-3 mb-8">
                <ShieldCheck className="w-6 h-6 text-emerald-700 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-emerald-900">Verified authentic</div>
                  <div className="text-sm text-emerald-800">
                    This certificate was issued by {state.cert.issuer || "All Things Social Impact"}.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Award className="w-10 h-10 text-brand-orange flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest font-bold text-brand-orange">
                    {state.cert.kind === "course" ? "Full Toolbox Curriculum" : `Module ${state.cert.module_number}`}
                  </div>
                  <h1 data-testid="verify-title" className="font-serif text-2xl md:text-3xl text-brand-blue leading-tight mt-1">
                    {state.cert.kind === "course"
                      ? "Certificate of Completion — Full Toolbox"
                      : `Certificate of Completion — ${state.cert.module_title}`}
                  </h1>
                </div>
              </div>

              <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 text-sm">
                <div>
                  <dt className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Awarded to</dt>
                  <dd data-testid="verify-learner" className="text-slate-900 font-semibold">{state.cert.learner_name || "—"}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Issued</dt>
                  <dd data-testid="verify-issued" className="text-slate-900">{formatIssued(state.cert.issued_at)}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Certificate ID</dt>
                  <dd data-testid="verify-cert-id" className="text-slate-900 font-mono text-xs break-all">{state.cert.id}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Issued by</dt>
                  <dd className="text-slate-900">{state.cert.issuer || "All Things Social Impact"}</dd>
                </div>
              </dl>

              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-3">Share credential</div>
                <a
                  data-testid="verify-linkedin-share"
                  href={buildLinkedInAddUrl(state.cert)}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-[#0A66C2] hover:bg-[#004182] text-white px-5 py-2.5 text-sm font-semibold transition-colors"
                >
                  <Linkedin className="w-4 h-4" /> Add to LinkedIn profile
                </a>
                <p className="mt-2 text-xs text-slate-500">
                  Opens LinkedIn's Add to Profile flow with your certificate details pre-filled.
                </p>
              </div>
            </div>
          )}
        </div>

        <p className="mt-6 text-xs text-slate-500 text-center">
          Certificates are issued by All Things Social Impact for completion of the Social Impact Assessment Toolbox curriculum led by Andrea Kanaris.
        </p>
      </div>
    </div>
  );
}
